import { AppDispatch, IAppThunk, IRootState } from "../app/store";
import { z } from "zod";
import {
  addNonPublischedDevicesFromAlgosInRedux,
  addSelectedRoomToRedux,
  clearNonPublischedDevicesFromAlgosInRedux,
  getHeightFromFirstCeilingGrid,
  removeNonPublischedDevicesFromAlgosInRedux,
  removeSelectedRoomFromRedux,
  resetReviewModeInRedux,
  resetSpaceValidationRunInRedux,
  setActiveRoomColoringIdInRedux,
  setActiveLeftSideBarTabInRedux,
  setActiveModuleInRedux,
  setCopySpaceModeActiveInRedux,
  setCountAlgoSpaceComplete,
  setCountAlgoSpaceFailed,
  setCountAlgoSpaceTotal,
  setDragSelectSpacesInRedux,
  setMeasurementToolActiveInRedux,
  setRfpSpecifiedArticlesResponsibilityInRedux,
  setSelectedRoomsInRedux,
  setSelectedRoomTabInRedux,
  setSelectedSpaceNumberInRedux,
  thunkToast,
} from "./designSlice";
import { selectDeviceById, selectHttpDevices } from "./devices/deviceSelectors";
import {
  clickOnDeviceThunk,
  copyDeviceThunk,
  rotateDeviceThunk,
  setSavingDevices,
} from "./devices/deviceSlice";
import { IRoomSummary } from "@backend/src/shared/schemas/schemas";
import { openapi } from "../../openapi";
import {
  RIGHT_CLICK_DEVICE_MENU_ENUM,
  DEVICE_DISCIPLINE_MAPPING,
  DisciplineEnum,
} from "@multiconsult-group/multiplacer-core";
import { selectActiveSingleRoomSummary } from "../Room/roomMcbimSharedSelectors";
import { generateRandomString, PartialMcBimSpace } from "../utils";
import { IDevice, IMLPredictDevice, IMultiplacerModuleKey } from "@multiconsult-group/multiplacer-core";
import { ROOM_COLORING_IDS } from "../RoomColoring";
import { DesignLeftSideBarTabs, RoomSideBarTabs } from "@multiconsult-group/multiplacer-core";
import { copyDevicesFromASpaceToAnotherSpace } from "@multiconsult-group/multiplacer-core";
import {
  ROOM_SIDEBAR_DEFAULT_TAB_BY_MODULE,
  LEFT_SIDEBAR_DEFAULT_TAB_BY_MODULE,
  MULTIPLACER_MODULES_BY_KEY,
} from "@multiconsult-group/multiplacer-core";
import { selectMcBimItemsForViewer } from "../McBim/mcbimItemSelectors";
import { selectFinalPixPolygons } from "./pixpolygonSelectors";
import {
  setClickedMcBimItemIdInRedux,
  setSectionCenterCoordinateInRedux,
  setSelectedSectionInRedux,
  type IViewerSelectedSection,
} from "../ViewerRedux/viewerSlice";
import { selectHttpGetRoomsCurrentFloorResult } from "../Room/httpRoomSelectors";
import { selectHttpMcbimSpacesResult } from "../McBim/httpMcBimSelectors";
import {
  addAutoPipeRoutingConnectionPointInRedux,
  selectAutoPipeRoutingPickingConnectionPointInViewer,
} from "./pipeRoutingSlice";
import { withWorldZInCoordinate } from "./devices/deviceUtils";
import { selectCachedMcbimBuildingStoriesForCurrentProject } from "./designSelector";
import { getRoomSectionCoordinate } from "../RoomSectionViews/roomSectionViewSelectors";

const getPolygonCenterFromBoundingBox = (polygon: number[][]): [number, number] => {
  if (polygon.length === 0) {
    throw new Error("Polygon must contain at least one point");
  }

  const bounds = polygon.reduce(
    (acc, [x, y]) => ({
      minX: Math.min(acc.minX, x),
      maxX: Math.max(acc.maxX, x),
      minY: Math.min(acc.minY, y),
      maxY: Math.max(acc.maxY, y),
    }),
    {
      minX: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
    }
  );

  return [(bounds.minX + bounds.maxX) / 2, (bounds.minY + bounds.maxY) / 2];
};

export const thunkClickOnAPixPolygon = (
  pixPolygonId: string | undefined,
  _coordinates: [number, number]
): IAppThunk => {
  return (dispatch, getState) => {
    const state = getState();
    const mcbimSpaces = selectHttpMcbimSpacesResult(state);

    if (selectAutoPipeRoutingPickingConnectionPointInViewer(state)) {
      const clickedSpace = mcbimSpaces.find((space) => space.name === pixPolygonId);
      const polygon = clickedSpace?.polygon as number[][] | undefined;

      if (!clickedSpace || !polygon || polygon.length === 0) {
        return;
      }

      const [centerX, centerY] = getPolygonCenterFromBoundingBox(polygon);
      dispatch(
        addAutoPipeRoutingConnectionPointInRedux([
          centerX,
          centerY,
          state.design.floorAndJob?.elevation ?? 0,
        ])
      );
      return;
    }

    if (pixPolygonId == null) {
      return;
    }

    const roomSectionDirectionPrefix = "room-section-direction-";
    if (pixPolygonId.startsWith(roomSectionDirectionPrefix)) {
      const selectedSection = pixPolygonId.slice(
        roomSectionDirectionPrefix.length
      ) as IViewerSelectedSection;
      dispatch(setSelectedSectionInRedux(selectedSection));
      return;
    }

    const devices = selectHttpDevices(state);

    const device = devices.find((d) => d._id === pixPolygonId);
    if (device != null) {
      dispatch(clickOnDeviceThunk(device));
    }
    const space = mcbimSpaces.find((s) => s.name === pixPolygonId);
    if (space != null) {
      // Clicked on a space
      dispatch(thunkClickOnSpace(space.name));
      return;
    }

    const pixPolygons = selectFinalPixPolygons(state);
    const pixPolygon = pixPolygons.find((p) => p.id === pixPolygonId);

    const mcbimItmes = selectMcBimItemsForViewer(state);
    const bimItem = mcbimItmes.find((item) => item._id === pixPolygon?.entityId);
    if (bimItem) {
      dispatch(setClickedMcBimItemIdInRedux(bimItem._id));
      dispatch(setActiveLeftSideBarTabInRedux(DesignLeftSideBarTabs.MCBIMITEM_INFO));
    }
  };
};

export const thunkPublishAlgoDevices = (): IAppThunk => {
  return async (dispatch, getState) => {
    const devices = getState().design.nonPublischedDevicesFromAlgos;
    const formattedDevices = devices.map((device) => {
      const copyOfDevice = structuredClone(device);
      delete copyOfDevice._id;
      delete copyOfDevice.boundingBox;
      return copyOfDevice;
    });

    dispatch(setSavingDevices(true));

    try {
      await dispatch(
        openapi.endpoints.postMultiplacerDeviceAddDevices.initiate({
          body: {
            devices: formattedDevices,
          },
        })
      ).unwrap();
      dispatch(openapi.util.invalidateTags(["devices"]));
      dispatch(clearDesignThunk());

      dispatch(thunkToast({ text: "Devices copied", header: "Success", isSuccess: true }));
    } finally {
      dispatch(setSavingDevices(false));
    }
  };
};

export const thunkClickOnSpace = (spaceNr: string | undefined): IAppThunk => {
  return (dispatch, getState) => {
    if (spaceNr == null) {
      return;
    }
    const mcbimSpaces = selectHttpMcbimSpacesResult(getState());
    const rooms = selectHttpGetRoomsCurrentFloorResult(getState());
    const devices = selectHttpDevices(getState());
    const selectedSpaceNumber = getState().design.selectedSpaceNumber;
    const shiftSelectRooms = getState().design.shiftSelectRooms;
    const selectedRooms = getState().design.selectedRooms;
    const copySpaceMode = getState().design.copySpaceModeActive;
    const activeModule = getState().design.activeModule;
    const selectedRoomTab = getState().design.selectedRoomTab;
    const selectedSpace = mcbimSpaces.find((space) => space.name === spaceNr);
    const selectedSpacePolygon = selectedSpace?.polygon;

    if (copySpaceMode) {
      const spaceToCopyTo = mcbimSpaces.find((space) => space.name === spaceNr);

      if (spaceToCopyTo == null) {
        return;
      }
      const spaceToCopyFrom = mcbimSpaces.find((space) => space.name === selectedSpaceNumber);
      if (spaceToCopyFrom == null) {
        console.log("spaceToCopyFrom is null");
        return;
      }
      const roomToCopyTo = rooms?.[spaceNr];
      dispatch(
        thunkCopyDevicesFromSpaceToOtherOnSameFloor(
          spaceToCopyFrom,
          spaceToCopyTo,
          devices,
          roomToCopyTo
        )
      );
      return;
    }

    if (shiftSelectRooms) {
      if (selectedRooms.includes(spaceNr) === false) {
        dispatch(addSelectedRoomToRedux(spaceNr));
      }
      if (selectedRooms.includes(spaceNr)) {
        dispatch(removeSelectedRoomFromRedux(spaceNr));
      }
    } else {
      if (selectedRooms.length === 1) {
        dispatch(setSelectedRoomsInRedux([spaceNr]));
      }
      if (selectedRooms.length === 1 && selectedRooms[0] === spaceNr) {
        dispatch(setSelectedRoomsInRedux([]));
      }
      dispatch(setSelectedSpaceNumberInRedux(spaceNr));
      dispatch(setSectionCenterCoordinateInRedux(getRoomSectionCoordinate(selectedSpacePolygon)));
      if (activeModule && selectedRoomTab == null) {
        dispatch(setSelectedRoomTabInRedux(ROOM_SIDEBAR_DEFAULT_TAB_BY_MODULE[activeModule]));
      }
    }
  };
};

export const thunkHandleRightMenu =
  (action: RIGHT_CLICK_DEVICE_MENU_ENUM, polygonId: string): IAppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const device = selectDeviceById(state, polygonId);
    if (!device) {
      return;
    }
    const room = selectActiveSingleRoomSummary(state);
    const roomIsLocked = !!room?.multiplacerCeilingDevicesLocked?.bool;

    if (roomIsLocked) {
      // NB-UI: Norska texter i UI enligt din standard
      alert("Rommet er låst");
      return;
    }

    switch (action) {
      case RIGHT_CLICK_DEVICE_MENU_ENUM.DELETE: {
        // Använd RTK Query utan hooks i thunks:
        // (Byt endpointnamn till ditt faktiska)
        await dispatch(
          openapi.endpoints.deleteMultiplacerDeviceDeleteDevice.initiate({ deviceId: polygonId })
        ).unwrap();
        return;
      }
      case RIGHT_CLICK_DEVICE_MENU_ENUM.COPY: {
        await dispatch(copyDeviceThunk(polygonId));
        return;
      }
      case RIGHT_CLICK_DEVICE_MENU_ENUM.ROTATE: {
        await dispatch(rotateDeviceThunk({ device, angle: 90 }));
        return;
      }
      default: {
        // Gör switch:en “exhaustive”
        const _exhaustive: never = action;
        return _exhaustive;
      }
    }
  };

/** Minsta kontext som behövs för att fylla kraven i ZDevice */
const ZDeviceContext = z.object({
  projectNo: z.string(),
  floor: z.string(),
  roomName: z.string().optional(),
  roomNumber: z.string(),
  description: z.string().optional(),
  compCode: z.string().optional(),
});
export type IDeviceContext = z.infer<typeof ZDeviceContext>;

export const convertMlResponseToDevice = (ml: IMLPredictDevice, ctx: IDeviceContext): IDevice => {
  const { projectNo, floor, roomName, roomNumber } = ctx;
  return {
    _id: generateRandomString(10),
    compCode: ctx.compCode ?? "",
    coordinate: ml.coordinate,
    description: ctx.description ?? "",
    deviceCategory: ml.deviceCategory,
    direction: ml.direction,
    floor,
    discipline: DEVICE_DISCIPLINE_MAPPING[ml.deviceCategory] ?? DisciplineEnum.UNKNOWN,
    projectNo,
    roomName,
    roomNumber,
    bottomEdgeHeightAboveFloorMm: 2700,
  };
};

export const convertMlBatchToDevices = (mls: IMLPredictDevice[], ctx: IDeviceContext): IDevice[] =>
  mls.map((ml) => convertMlResponseToDevice(ml, ctx));

export const changeActiveModuleThunk =
  (nextModule: IMultiplacerModuleKey): IAppThunk =>
  (dispatch, getState) => {
    const prevModule = getState().design.activeModule;

    const selectedSpaceNumber = getState().design.selectedSpaceNumber;

    const spaces = selectHttpMcbimSpacesResult(getState());

    if (selectedSpaceNumber == null && spaces.length > 0) {
      const randomSpace = spaces[Math.floor(Math.random() * spaces.length)].name;
      dispatch(setSelectedSpaceNumberInRedux(randomSpace));
    }

    // 1) Alltid uppdatera modulvärdet
    dispatch(setActiveModuleInRedux(nextModule));

    // 2) Om vi stänger alla moduler -> stäng även vänsterpanel
    if (!nextModule) {
      dispatch(setActiveLeftSideBarTabInRedux(undefined));
      return;
    }

    // 3) Bara om modulen faktiskt ändrades vill vi sätta default-tab
    if (nextModule !== prevModule) {
      dispatch(setActiveLeftSideBarTabInRedux(LEFT_SIDEBAR_DEFAULT_TAB_BY_MODULE[nextModule]));
      dispatch(setSelectedRoomTabInRedux(ROOM_SIDEBAR_DEFAULT_TAB_BY_MODULE[nextModule]));
    }
    dispatch(
      setActiveRoomColoringIdInRedux(MULTIPLACER_MODULES_BY_KEY[nextModule].defaultRoomColoringId)
    );
  };

export const dragSelectEndThunk = (ids: string[]): IAppThunk => {
  return (dispatch, getState) => {
    dispatch(setSelectedRoomsInRedux(ids));
    dispatch(setDragSelectSpacesInRedux(false));
  };
};
export const clearDesignThunk = (): IAppThunk => {
  return (dispatch, getState) => {
    const reviewIsActive = getState().design.review.active;
    const algosInProgress = getState().design.algosInProgress;

    const aSpaceBarIsActive = reviewIsActive || algosInProgress;

    if (!aSpaceBarIsActive) {
      dispatch(setSelectedRoomsInRedux([]));
    }

    dispatch(clearNonPublischedDevicesFromAlgosInRedux());
    dispatch(setCountAlgoSpaceTotal(0));
    dispatch(setCountAlgoSpaceComplete(0));
    dispatch(setCountAlgoSpaceFailed(0));
    dispatch(resetSpaceValidationRunInRedux());
    dispatch(setDragSelectSpacesInRedux(false));
    dispatch(resetReviewModeInRedux());
    dispatch(setActiveRoomColoringIdInRedux(undefined));
    dispatch(setMeasurementToolActiveInRedux(false));
    dispatch(setCopySpaceModeActiveInRedux(false));
  };
};
export interface IRoomMcBimSpaceCombo {
  roomNumber: string;
  rfpMalRom: string;
  polygon: number[][];
  ceilingCoordinate?: number[];
  ceilingDirection?: number[];
}

export const thunkCopyDevicesFromSpaceToOtherOnSameFloor = (
  spaceToCopyFrom: PartialMcBimSpace,
  spaceToCopyTo: PartialMcBimSpace,
  devices: IDevice[],
  roomToCopyTo?: IRoomSummary
): IAppThunk => {
  return (dispatch, getState) => {
    if (spaceToCopyTo.name === spaceToCopyFrom.name) {
      return;
    }

    const nonPublischedDevicesFromAlgos = getState().design.nonPublischedDevicesFromAlgos;
    const devicesExistsInSpaceToCopyTo = devices.find(
      (device) => device.roomNumber === spaceToCopyTo.name && device.coordinate != null
    );
    if (devicesExistsInSpaceToCopyTo != null) {
      dispatch(
        thunkToast({
          text: "Det finnes allerede enheter i rommet du prøver å kopiere til",
          header: "Feil",
          isSuccess: false,
        })
      );
      return;
    }

    const existingDevicesInNonPublischedDevices = nonPublischedDevicesFromAlgos.filter(
      (device) => device.roomNumber === spaceToCopyTo.name
    );

    if (existingDevicesInNonPublischedDevices.length === 0) {
      const devicesTopCopy = devices.filter((device) => device.roomNumber === spaceToCopyFrom.name);
      const copiedDevices = copyDevicesFromASpaceToAnotherSpace({
        devicesToCopy: devicesTopCopy,
        spaceToCopyFrom,
        spaceToCopyTo,
        roomToCopyTo: roomToCopyTo,
      });
      if (copiedDevices.length === 0) {
        dispatch(
          thunkToast({
            text: `Ingen enheter kunne kopieres fordi målrommet ${spaceToCopyTo.name} skiller seg for mye fra kildrommet ${spaceToCopyFrom.name}.`,
            header: "Advarsel",
            isSuccess: false,
          })
        );
        return;
      }

      const activeFloorElevationM = getState().design.floorAndJob?.elevation;
      const buildingStories = selectCachedMcbimBuildingStoriesForCurrentProject(getState());
      const buildingStory = buildingStories.find(
        (story) => story.name === spaceToCopyTo.buildingStory?.name
      );
      const heightFromFloor =
        getHeightFromFirstCeilingGrid(spaceToCopyTo.ceilingGrids ?? []) ?? 2700;
      const floorElevationM = buildingStory?.elevation ?? activeFloorElevationM;

      const enrichedCopiedDevices = copiedDevices.map((device) => ({
        ...device,
        floor: spaceToCopyTo.buildingStory?.name ?? device.floor,
        roomName: spaceToCopyTo.longName ?? device.roomName,
        bottomEdgeHeightAboveFloorMm: heightFromFloor,
        coordinate: withWorldZInCoordinate(device.coordinate, floorElevationM, heightFromFloor),
      }));

      dispatch(addNonPublischedDevicesFromAlgosInRedux(enrichedCopiedDevices));
    } else {
      dispatch(
        removeNonPublischedDevicesFromAlgosInRedux(
          existingDevicesInNonPublischedDevices.map((device) => device._id)
        )
      );
    }
  };
};

export const setActiveRoomColoringIdThunk =
  (id?: string | ROOM_COLORING_IDS | undefined) =>
  (dispatch: AppDispatch, getState: () => IRootState): void => {
    const state = getState();
    const prev = state.design.activeRoomColoringId;

    const rooms = selectHttpGetRoomsCurrentFloorResult(state);

    // Ingen endring => no-op (hindrer unødvendige rerenders)
    if (prev === id) return;

    if (id != null && id?.startsWith("specifiedArticle")) {
      dispatch(setSelectedRoomTabInRedux(RoomSideBarTabs.ROOM_SPECIFIED_ARTICLES));
      dispatch(
        setSelectedSpaceNumberInRedux(rooms ? Object.values(rooms)[0]?.roomNumber : undefined)
      );
      // ta ut värdet efter första punkten
      const responsibility = id.split(".")[1];
      dispatch(setRfpSpecifiedArticlesResponsibilityInRedux(responsibility));
      if (responsibility === "overall") {
        dispatch(setRfpSpecifiedArticlesResponsibilityInRedux(null));
      }
    }

    // ---------------------------------------------------------------------------
    // Fremtidig logikk:
    // - resette andre paneler
    // - logge analytics
    // - sikre at id finnes i tilgjengelige filtre
    // - defaulting mellom standardrom/beslutning/spesifiserte artikler
    // ---------------------------------------------------------------------------

    dispatch(setActiveRoomColoringIdInRedux(id));
  };
